"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import Image from "next/image";
import { Star } from "lucide-react";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { promptCardCategoryBadgeClass } from "@/components/aios-command/chat/preset-prompt-cards";
import {
  useFavoritePresets,
  usePresetParentCategories,
  usePresets,
  presetParentCategoryPillLabel,
} from "@/hooks/use-presets";

const CATEGORY_FAVOURITES = "Favourites";
const CATEGORY_CUSTOM = "Custom";

/** Neutral toolbar pills — no purple fill (composer shell handles focus glow). */
export const COMPOSER_TOOLBAR_PILL_CLASS =
  "inline-flex h-7 items-center gap-1.5 rounded-full border border-border bg-background px-3 text-xs font-medium text-foreground/80 transition-colors hover:border-border/80 hover:bg-muted/60 hover:text-foreground disabled:pointer-events-none disabled:opacity-45 dark:bg-background dark:hover:bg-muted/40";

interface ComposerPresetsButtonProps {
  onSelectPrompt: (prompt: string) => void;
  disabled?: boolean;
  className?: string;
}

export function ComposerPresetsButton({
  onSelectPrompt,
  disabled,
  className,
}: ComposerPresetsButtonProps) {
  const [open, setOpen] = useState(false);
  const [activeCategory, setActiveCategory] = useState("");

  const { data: presetParentsResponse, isLoading: isLoadingParents } =
    usePresetParentCategories({ enabled: open });
  const parentCategoryItems = presetParentsResponse?.items ?? [];

  useEffect(() => {
    if (!open) return;
    setActiveCategory((prev) => {
      if (prev) return prev;
      return parentCategoryItems[0]?.id ?? CATEGORY_CUSTOM;
    });
  }, [open, parentCategoryItems]);

  const parentCategoryId = useMemo(() => {
    if (
      activeCategory === CATEGORY_FAVOURITES ||
      activeCategory === CATEGORY_CUSTOM ||
      !activeCategory
    ) {
      return undefined;
    }
    return parentCategoryItems.some((i) => i.id === activeCategory)
      ? activeCategory
      : undefined;
  }, [activeCategory, parentCategoryItems]);

  const {
    promptItems: apiPromptItems,
    isLoading: isLoadingPresets,
    isFetchingNextPage,
    hasNextPage,
    loadMoreRef,
  } = usePresets({
    parentCategoryId,
    enabled: open && Boolean(parentCategoryId),
  });

  const {
    promptItems: myPresetPromptItems,
    isLoading: isLoadingMyPresets,
    isFetchingNextPage: isFetchingNextMyPresets,
    hasNextPage: hasNextMyPresets,
    loadMoreRef: myPresetsLoadMoreRef,
  } = usePresets({
    myPresets: true,
    enabled: open && activeCategory === CATEGORY_CUSTOM,
  });

  const {
    favoritePromptItems,
    isLoading: isLoadingFavorites,
  } = useFavoritePresets({
    enabled: open && activeCategory === CATEGORY_FAVOURITES,
  });

  const prompts = useMemo(() => {
    if (activeCategory === CATEGORY_FAVOURITES) return favoritePromptItems;
    if (activeCategory === CATEGORY_CUSTOM) return myPresetPromptItems;
    return apiPromptItems;
  }, [
    activeCategory,
    favoritePromptItems,
    myPresetPromptItems,
    apiPromptItems,
  ]);

  const isLoading =
    activeCategory === CATEGORY_CUSTOM
      ? isLoadingMyPresets
      : activeCategory === CATEGORY_FAVOURITES
        ? isLoadingFavorites
        : isLoadingPresets || (isLoadingParents && !activeCategory);

  const showLoadMore =
    activeCategory === CATEGORY_CUSTOM
      ? hasNextMyPresets
      : activeCategory !== CATEGORY_FAVOURITES && hasNextPage;

  const activeLoadMoreRef =
    activeCategory === CATEGORY_CUSTOM ? myPresetsLoadMoreRef : loadMoreRef;

  const isFetchingMore =
    activeCategory === CATEGORY_CUSTOM
      ? isFetchingNextMyPresets
      : isFetchingNextPage;

  const categoryTabs = useMemo(
    () => [CATEGORY_CUSTOM, ...parentCategoryItems.map((i) => i.id)],
    [parentCategoryItems]
  );

  const handleSelect = useCallback(
    (prompt: string) => {
      onSelectPrompt(prompt);
      setOpen(false);
    },
    [onSelectPrompt]
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(COMPOSER_TOOLBAR_PILL_CLASS, className)}
          aria-label="Browse presets"
          onMouseDown={(e) => e.preventDefault()}
          disabled={disabled}
        >
          <Image
            src="/prompts.svg"
            alt=""
            width={14}
            height={14}
            unoptimized
            className="shrink-0 opacity-90 dark:invert"
          />
          <span>Presets</span>
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        side="top"
        sideOffset={8}
        className="w-[min(92vw,22rem)] p-0"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <div className="border-b border-border px-3 py-2.5">
          <p className="text-xs font-semibold text-foreground">Presets</p>
          <p className="mt-0.5 text-[11px] text-muted-foreground">
            Pick one to fill the composer
          </p>
        </div>

        <div className="flex gap-1.5 overflow-x-auto px-3 py-2 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
          <button
            type="button"
            onClick={() => setActiveCategory(CATEGORY_FAVOURITES)}
            className={cn(
              "inline-flex h-7 shrink-0 items-center gap-1 rounded-full border px-2.5 text-[11px] font-medium transition-colors",
              activeCategory === CATEGORY_FAVOURITES
                ? "border-primary bg-primary/10 text-primary"
                : "border-border text-muted-foreground hover:text-foreground"
            )}
          >
            <Star
              className={cn(
                "size-3",
                activeCategory === CATEGORY_FAVOURITES &&
                  "fill-amber-400 text-amber-500"
              )}
            />
            Favourites
          </button>
          {categoryTabs.map((cat) => {
            const parentItem = parentCategoryItems.find((i) => i.id === cat);
            const label =
              cat === CATEGORY_CUSTOM
                ? CATEGORY_CUSTOM
                : presetParentCategoryPillLabel(parentItem?.name ?? "");
            return (
              <button
                key={cat}
                type="button"
                onClick={() => setActiveCategory(cat)}
                className={cn(
                  "inline-flex h-7 shrink-0 items-center rounded-full border px-2.5 text-[11px] font-medium transition-colors",
                  activeCategory === cat
                    ? "border-primary bg-primary/10 text-primary"
                    : "border-border text-muted-foreground hover:text-foreground"
                )}
              >
                {label}
              </button>
            );
          })}
        </div>

        <div className="max-h-72 overflow-y-auto px-2 pb-2">
          {isLoading ? (
            <div className="space-y-2 p-1">
              {[0, 1, 2].map((i) => (
                <Skeleton key={i} className="h-16 w-full rounded-lg" />
              ))}
            </div>
          ) : prompts.length === 0 ? (
            <p className="px-2 py-8 text-center text-xs text-muted-foreground">
              No presets here yet
            </p>
          ) : (
            <div className="space-y-1.5">
              {prompts.map((item) => {
                const badgeLabel =
                  presetParentCategoryPillLabel(
                    item.parent_category ?? ""
                  ).toUpperCase() || "PRESET";
                return (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => handleSelect(item.prompt)}
                    className="flex w-full flex-col gap-1 rounded-lg border border-border/60 bg-card px-3 py-2.5 text-left transition-colors hover:border-primary/30 hover:bg-muted/40"
                  >
                    <div className="flex min-w-0 items-center gap-2">
                      <Badge
                        variant="secondary"
                        className={promptCardCategoryBadgeClass}
                      >
                        {badgeLabel}
                      </Badge>
                      <span className="min-w-0 flex-1 truncate text-[11px] text-muted-foreground">
                        {item.title}
                      </span>
                    </div>
                    <p className="line-clamp-2 text-xs font-medium leading-snug text-foreground">
                      {item.description || item.prompt}
                    </p>
                  </button>
                );
              })}
              {showLoadMore ? (
                <div ref={activeLoadMoreRef} className="h-4 w-full" />
              ) : null}
              {isFetchingMore ? (
                <Skeleton className="h-12 w-full rounded-lg" />
              ) : null}
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
